import type { ProviderManifest } from '../prerender/providerManifest';

interface ManifestPointer {
  hash: string;
  file: string;
}

const isPointer = (v: unknown): v is ManifestPointer =>
  !!v && typeof (v as ManifestPointer).hash === 'string' && /^provider-manifest\.[\w-]+\.json$/.test((v as ManifestPointer).file);

const isManifest = (v: unknown): v is ProviderManifest =>
  !!v && typeof (v as ProviderManifest).hash === 'string' && Array.isArray((v as ProviderManifest).ids);

/**
 * Latest provider manifest of the deployed site: the pointer `provider-manifest.latest.json` (revalidated, never
 * cached blindly) names the immutable `provider-manifest.{hash}.json`. Returns null on any failure or when the
 * page already has that manifest: the page then keeps the one it was prerendered with.
 */
export async function fetchLatestProviderManifest(currentHash: string | null, fetcher: typeof fetch = fetch): Promise<ProviderManifest | null> {
  try {
    const pointerRes = await fetcher('/provider-manifest.latest.json', { cache: 'no-cache' });
    if (!pointerRes.ok) return null;
    const pointer: unknown = await pointerRes.json();
    if (!isPointer(pointer) || pointer.hash === currentHash) return null;
    const res = await fetcher(`/${pointer.file}`);
    if (!res.ok) return null;
    const manifest: unknown = await res.json();
    return isManifest(manifest) ? manifest : null;
  } catch {
    return null;
  }
}
